import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { useContext } from 'react';

import { FloatingInput, FilterCriteriaRow, DateRangeFilter, LoaderAnimation } from 'src/components';

import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { TransactionFilterContext } from 'src/context/transaction-filter-context';

import { DownloadCSVContext } from 'src/context/download-csv-context';
import TealiumEvents from 'src/helpers/tealium-events';
import { getOltpTransactions } from 'src/services/oltp-transaction.service';

import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import InfoTrayForm from './info-tray-form';
import { TIME_RANGE, setTimesOnSelectedDates, isSameDayDateRange } from './time-range-helpers';
import './transactions-filter-form.scss';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import { transformTransactionsForCsv } from 'src/helpers/csv-string-builder';
import { MerchantProfileContext } from 'src/context/merchant-profile-context';

const inMiniProgram = MiniAppEvents.isInMiniApp();
const filterOptions = {
  transactionType: 'Transaction Type',
  responseCode: 'Response Codes',
  terminalId: 'Terminal ID',
  reference: '',
  fromAmount: null,
  toAmount: null,
  fromTime: TIME_RANGE[0],
  toTime: TIME_RANGE[TIME_RANGE.length - 1],
};

export default function TransactionsFilterForm({
  title,
  children,
  transactionTypes,
  responseCodes,
  terminalIds,
  callingPage,
  refundsActive,
  setRefundsActive,
  showRefunds,
}) {
  const { merchant } = useContext(MerchantProfileContext);
  const { transactionFilter, setTransactionFilter } = useContext(TransactionFilterContext);
  const { transactionTotalPages, lastLoadedPage, currentTransactions, companyName } =
    useContext(DownloadCSVContext);
  const [search, setSearch] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const handleTechnicalError = useTechnicalErrorHandler();

  function validateFilterForm(values) {
    setTransactionFilter({
      ...transactionFilter,
      dropdownFilters: {
        transactionType: values.transactionType,
        responseCode: values.responseCode,
        terminalId: values.terminalId,
      },
      selectedDates: setTimesOnSelectedDates(
        values.fromTime,
        values.toTime,
        transactionFilter.selectedDates,
      ),
    });
  }

  let searchCriteria = null;

  const getAllTransactions = async (page, totalPages, data = []) => {
    const transactionFetchAbortController = new AbortController();
    const transactionsResultWithTotalPages = await getOltpTransactions(
      transactionFilter.selectedDates[0],
      transactionFilter.selectedDates[1],
      page,
      transactionFetchAbortController,
      handleTechnicalError,
      false,
      true,
    );

    data = [...data, ...[transactionsResultWithTotalPages.Rows]];

    if (page <= totalPages) {
      return getAllTransactions(page + 1, totalPages, data);
    }

    return data;
  };

  const callTealiumClickEvent = () => {
    if (callingPage === 'terminal') {
      TealiumEvents.terminalTransactionsExportClicked();
    } else {
      if (showRefunds) {
        TealiumEvents.refundExportClicked();
      } else {
        TealiumEvents.accountTransactionsExportClicked();
      }
    }
  };

  if (
    transactionFilter.transactionSearch.fromAmount &&
    transactionFilter.transactionSearch.toAmount
  )
    searchCriteria = `R ${formatCurrencyAmountSimple(
      transactionFilter.transactionSearch.fromAmount,
    )} - R ${formatCurrencyAmountSimple(transactionFilter.transactionSearch.toAmount)}`;
  else if (transactionFilter.transactionSearch.reference)
    searchCriteria = transactionFilter.transactionSearch.reference;

  return (
    <div className="TransactionFilterForm">
      <div className="columns is-gapless title-container is-vcentered is-hidden-mobile">
        {title && (
          <span className="column page-title">
            {!refundsActive ? title : 'Transactions available for Refund'}
          </span>
        )}
        <div className="column columns is-narrow is-vcentered is-pulled-right">
          {searchCriteria && (
            <div className="column">
              <FilterCriteriaRow
                filterCriteria={[
                  {
                    value: searchCriteria,
                    clear: () =>
                      setTransactionFilter({
                        ...transactionFilter,
                        transactionSearch: {},
                      }),
                  },
                ]}
              />
            </div>
          )}
          {showRefunds && (
            <button
              className={`button ${
                !refundsActive ? 'is-primary' : 'back-refunds'
              } refunds-button is-rounded has-margin-right-5 is-pulled-right has-padding-4`}
              onClick={async () => {
                if (refundsActive) {
                  TealiumEvents.backToTransactions();
                } else {
                  TealiumEvents.goToRefundsPage();
                }
                setRefundsActive(!refundsActive);
              }}
            >
              {!refundsActive ? 'Refund a customer' : 'Back to Transactions'}
            </button>
          )}
          {search ? (
            <span
              className="column is-narrow close-search-button is-icon-5 icon-cross is-pulled-right"
              onClick={() => setSearch(false)}
            ></span>
          ) : (
            <span
              className="column is-narrow search-button is-icon-5 icon-search is-pulled-right"
              onClick={() => setSearch(true)}
            ></span>
          )}
        </div>
      </div>
      <div className="is-hidden-tablet has-padding-bottom-5">
        {showRefunds && (
          <button
            className={`button ${
              !refundsActive ? 'is-primary' : 'back-refunds'
            } refunds-button is-rounded`}
            onClick={async () => {
              if (refundsActive) {
                TealiumEvents.backToTransactions();
              } else {
                TealiumEvents.goToRefundsPage();
              }
              setRefundsActive(!refundsActive);
            }}
          >
            {!refundsActive ? 'Refund a customer' : 'Back to Transactions'}
          </button>
        )}
      </div>
      <div className="filter-title is-vcentered">
        <span className="is-hidden-mobile">Filter by</span>
        <span className="is-hidden-tablet">Filter by:</span>

        <div className="filter-buttons is-hidden-tablet has-margin-bottom-6 is-pulled-right is-vcentered">
          {!inMiniProgram && (
            <span
              className="item-icon-white is-clickable is-icon-6 icon-upload-thin is-pulled-right"
              onClick={onExportClicked}
            ></span>
          )}
          <span
            className="item-icon-white is-clickable is-icon-4 icon-filters "
            onClick={() => setSearch(true)}
          ></span>
        </div>
      </div>

      <FilterCriteriaRow
        className="is-hidden-tablet"
        showDate={true}
        filterCriteria={[
          {
            value: searchCriteria,
            clear: () =>
              setTransactionFilter({
                ...transactionFilter,
                transactionSearch: {},
              }),
          },
        ]}
      />
      <Formik
        initialValues={filterOptions}
        onSubmit={onExportClicked}
        validateOnChange={true}
        validate={validateFilterForm}
      >
        {({ values }) => {
          return (
            <Form
              className="columns is-multiline filter-form is-flex is-vcentered"
              data-testid="transaction-filter-form"
            >
              <div className="column is-11 is-hidden-mobile">
                <div className="columns is-0 is-variable is-multiline ">
                  <div className="column is-narrow select-filter has-margin-bottom-6">
                    <DateRangeFilter
                      tealiumEvent={
                        callingPage === 'terminal'
                          ? TealiumEvents.terminalTransactionsDateFilterApplied
                          : TealiumEvents.accountTransactionsDateFilterApplied
                      }
                    />
                  </div>
                  {callingPage === 'terminal' && (
                    <>
                      <div className="column is-narrow has-margin-right-5 time-filter has-margin-bottom-6">
                        <Field
                          name="fromTime"
                          type="text"
                          placeholder="Time from"
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInput}
                          items={
                            isSameDayDateRange(transactionFilter)
                              ? TIME_RANGE.slice(0, TIME_RANGE.length - 1)
                              : TIME_RANGE
                          }
                          getItemDisplayValue={(item) => item}
                          fieldType="select"
                          isDark={true}
                          hasNoAfterLine={true}
                          automationIdPrefix="From_Time_filter"
                          onItemSelect={(item) => TealiumEvents.transactionsTimeFilter(true, item)}
                        />
                        <div className="floating-dash">-</div>
                      </div>
                      <div className="column is-narrow has-margin-right-5 time-filter has-margin-bottom-6">
                        <Field
                          name="toTime"
                          type="text"
                          placeholder="Time to"
                          component={MiniAppFocusWrapper}
                          innerComponent={FloatingInput}
                          items={
                            isSameDayDateRange(transactionFilter)
                              ? TIME_RANGE.slice(TIME_RANGE.indexOf(values.fromTime) + 1)
                              : TIME_RANGE
                          }
                          getItemDisplayValue={(item) => item}
                          fieldType="select"
                          isDark={true}
                          hasNoAfterLine={true}
                          automationIdPrefix="To_Time_filter"
                          onItemSelect={(item) => TealiumEvents.transactionsTimeFilter(false, item)}
                        />
                      </div>

                      {transactionTypes && (
                        <div className="column is-narrow select-filter has-margin-bottom-6">
                          <Field
                            name="transactionType"
                            type="text"
                            placeholder="Transaction Type"
                            component={MiniAppFocusWrapper}
                            innerComponent={FloatingInput}
                            items={transactionTypes ? transactionTypes : []}
                            getItemDisplayValue={(item) => item}
                            filterItemsWithInputValue={true}
                            fieldType="select"
                            isDark={true}
                            hasNoAfterLine={true}
                            automationIdPrefix="Terminal_Transactions_Filter"
                            onItemSelect={(item) =>
                              callingPage === 'terminal'
                                ? TealiumEvents.terminalTransactionsTransactionFilterApplied(item)
                                : TealiumEvents.accountTransactionsTransactionFilterApplied(item)
                            }
                          />
                        </div>
                      )}
                      {responseCodes && (
                        <div className="column is-narrow select-filter has-margin-right-6 has-margin-bottom-6">
                          <Field
                            name="responseCode"
                            type="text"
                            placeholder="Response Codes"
                            component={MiniAppFocusWrapper}
                            innerComponent={FloatingInput}
                            items={responseCodes ? responseCodes : []}
                            getItemDisplayValue={(item) => item}
                            filterItemsWithInputValue={true}
                            fieldType="select"
                            isDark={true}
                            hasNoAfterLine={true}
                            automationIdPrefix="Terminal_Transactions_Filter"
                          />
                        </div>
                      )}
                      {terminalIds && (
                        <div className="column is-narrow select-filter has-margin-right-6 has-margin-bottom-6">
                          <Field
                            name="terminalId"
                            type="text"
                            placeholder="Terminal IDs"
                            component={MiniAppFocusWrapper}
                            innerComponent={FloatingInput}
                            items={terminalIds ? terminalIds : []}
                            getItemDisplayValue={(item) => item}
                            filterItemsWithInputValue={true}
                            fieldType="select"
                            isDark={true}
                            hasNoAfterLine={true}
                            automationIdPrefix="Terminal_Transactions_Filter"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div className="column export-button-container is-hidden-mobile has-margin-bottom-6">
                {isExporting ? (
                  <LoaderAnimation width={'30px'} height={'30px'} />
                ) : (
                  <button
                    data-testid="transaction-filter-form-export-button"
                    className="button export-button is-pulled-right"
                    type="submit"
                    automationid="Terminal_Transactions_Button_Export"
                  >
                    <span className="is-icon-6 has-padding-x-6 icon-upload"></span>
                  </button>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
      <div className="is-hidden-mobile">
        <InfoTrayForm
          search={search}
          setSearch={setSearch}
          isMobile={false}
          callingPage={callingPage}
        />
      </div>
      <div className="is-hidden-tablet">
        <InfoTrayForm
          search={search}
          setSearch={setSearch}
          isMobile={true}
          callingPage={callingPage}
        />
      </div>
      {children}
    </div>
  );

  async function onExportClicked() {
    setIsExporting(true);
    const transactions = await getAllTransactions(
      currentTransactions.length ? lastLoadedPage + 1 : 1,
      transactionTotalPages,
      currentTransactions,
    );

    setIsExporting(false);

    callTealiumClickEvent();
    const csvData = transformTransactionsForCsv(
      companyName || merchant?.companyName,
      transactionFilter.selectedDates,
      transactions.flat(),
      true,
    );

    if (navigator.msSaveBlob) {
      // Required Microsoft Edge <= 44.18362.449.0
      var blob = new Blob([csvData.csvString], { type: 'text/plain;charset=utf-8;' });
      navigator.msSaveBlob(blob, csvData.fileName);
    } else {
      const encodedUri = encodeURI('data:text/csv;charset=utf-8,' + csvData.csvString);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', csvData.fileName);
      document.body.appendChild(link); // Required for FireFox
      link.click();
    }
  }
}
